import React, { useEffect, useState } from 'react'
import { useAuth } from './Auth'
import db from '../../../../services/firebase'
import { doc, getDoc } from 'firebase/firestore'
import { getLastActivity, initSessionTracking } from './SessionUtils'

const DEFAULT_TIMEOUT = 12 * 60 * 60 * 1000

interface SessionTimeoutManagerProps {
  children: React.ReactNode
}

const SessionTimeoutManager: React.FC<SessionTimeoutManagerProps> = ({ children }) => {
  const { currentUser, logout } = useAuth()
  const [timeoutDuration, setTimeoutDuration] = useState<number>(DEFAULT_TIMEOUT)
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null)

  useEffect(() => {
    const cleanupTracking = initSessionTracking()
    return cleanupTracking
  }, [])

  useEffect(() => {
    if (!currentUser?.tenantId) return
    
    let isMounted = true
    
    const cachedTimeoutKey = `sessionTimeout_${currentUser.tenantId}`
    const cachedTimeout = localStorage.getItem(cachedTimeoutKey)
    const cachedTimestamp = localStorage.getItem(`${cachedTimeoutKey}_timestamp`)
    
    if (cachedTimeout && cachedTimestamp) {
      const timestamp = parseInt(cachedTimestamp, 10)
      const now = Date.now()
      const oneHour = 60 * 60 * 1000
      
      if (now - timestamp < oneHour) {
        setTimeoutDuration(parseInt(cachedTimeout, 10))
        return
      }
    }
    
    const fetchTimeoutSetting = async () => {
      try {
        const tenantId = currentUser?.tenantId || ''
        const tenantRef = doc(db, 'tenants', tenantId)
        const securityRef = doc(tenantRef, 'settings', 'security')
        
        const securityDoc = await getDoc(securityRef)
        
        if (isMounted && securityDoc.exists() && securityDoc.data().sessionTimeoutMs) {
          const timeout = securityDoc.data().sessionTimeoutMs
          setTimeoutDuration(timeout)
          
          localStorage.setItem(cachedTimeoutKey, timeout.toString())
          localStorage.setItem(`${cachedTimeoutKey}_timestamp`, Date.now().toString())
        }
      } catch (error) {
        console.error('Error fetching timeout setting:', error)
      }
    }

    fetchTimeoutSetting()
    
    return () => {
      isMounted = false
    }
  }, [currentUser?.tenantId])

  useEffect(() => {
    if (!currentUser) return

    const checkInactivity = () => {
      const lastActivity = getLastActivity()
      if (!lastActivity) return
      
      const currentTime = Date.now()
      const inactiveTime = currentTime - lastActivity

      if (inactiveTime >= timeoutDuration) {
        logout().catch(error => {
          console.error('Error during logout:', error)
        })
        // alert('Your session has expired due to inactivity. Please log in again.')
      }
    }

    const id = setInterval(checkInactivity, 60000)
    
    setTimeoutId(id)

    return () => {
      clearInterval(id)
    }
  }, [currentUser, logout, timeoutDuration])

  return <>{children}</>
}

export default SessionTimeoutManager